import { State } from '@progress/kendo-data-query';
import { ProductListFilterRequest } from 'src/types/catalog';
import { IPostBody } from 'src/types/PostBody';

export const queryKeys = {
  company: {
    root: ['company'] as const,
    clients: (companyId: string) => [...queryKeys.company.root, companyId, 'clients'] as const,
    attributes: (companyId: number) =>
      [...queryKeys.company.root, companyId, 'attributes'] as const,
    managementCompanies: () => [...queryKeys.company.root, 'management'] as const,
  },
  department: {
    root: ['department'] as const,
    departmentList: (companyId: string) => [...queryKeys.department.root, companyId] as const,
  },
  glCode: {
    root: ['glCode'] as const,
    all: (companyId: string) => [...queryKeys.glCode.root, companyId, 'all'] as const,
    list: (companyId: string, glPeriodId?: string | null) =>
      [...queryKeys.glCode.root, companyId, 'list', glPeriodId] as const,
  },
  product: {
    root: ['catalogProducts'] as const,
    catalogs: () => [...queryKeys.product.root, 'catalogs'] as const,
    categories: (catalogId: number | null) =>
      [...queryKeys.product.root, 'categories', catalogId] as const,
    externalCatalog: (catalogId: number) =>
      [...queryKeys.product.root, 'externalCatalog', catalogId] as const,
    offlineSuppliers: (catalogId: number | null) =>
      [...queryKeys.product.root, 'offlineSuppliers', catalogId] as const,
    productDetails: (
      productId: number | null,
      catalogId: number | null,
      categoryId: number | null
    ) => [...queryKeys.product.root, 'productDetails', productId, catalogId, categoryId] as const,
    productList: (request: ProductListFilterRequest & { searchText?: string }) =>
      [...queryKeys.product.root, 'productList', request] as const,
  },
  invoice: {
    root: ['invoice'] as const,
    list: (companyId: string) => [...queryKeys.invoice.root, companyId, 'list'] as const,
    allList: (companyId: string) => [...queryKeys.invoice.root, companyId, 'allList'] as const,
    linkedReceipt: (companyId: string, body: State) =>
      [...queryKeys.invoice.root, companyId, 'linkedReceipt', JSON.stringify(body)] as const,
  },
  receipt: {
    root: ['receipt'] as const,
    list: (companyId: string) => [...queryKeys.receipt.root, companyId, 'list'] as const,
    lineItems: (receiptId: number) => [...queryKeys.receipt.root, receiptId, 'lineItems'] as const,
    receiptList: (companyId: string, postBody: IPostBody) =>
      [...queryKeys.receipt.root, companyId, 'receiptList', postBody] as const,
  },
  po: {
    root: ['po'] as const,
    lineItems: (poId: number, postBody: any) =>
      [...queryKeys.po.root, poId, 'lineItems', postBody] as const,
    lineItem: (poId: number, lineItemId: number, itemNum?: string) =>
      [...queryKeys.po.root, poId, 'lineItem', lineItemId, itemNum] as const,
    notRecommendedLineItems: (poId: number, lineItemId?: number | null) =>
      [...queryKeys.po.root, poId, lineItemId, 'notRecommendedLineItems'] as const,
    poList: (companyId: string, postBody: any, itemNumber?: string) =>
      [...queryKeys.po.root, companyId, 'poList', postBody, itemNumber] as const,
  },
  supplier: {
    root: ['supplier'] as const,
    offline: () => [...queryKeys.supplier.root, 'offline'] as const,
  },
  cart: {
    root: ['cart'] as const,
    notRecommendedItems: () => [...queryKeys.cart.root, 'notRecommendedItems'] as const,
  },
  favorite: {
    root: ['favorite'] as const,
    folders: () => [...queryKeys.favorite.root, 'folders'] as const,
  },
  workArea: {
    root: ['workArea'] as const,
    companyTree: (rootCompanyId: string | number | null, clientId: string | number | null) =>
      [...queryKeys.workArea.root, 'companyTree', rootCompanyId, clientId] as const,
    companyUsersTree: (
      companyId: string | number | null,
      clientId: string | number | null
    ) => [...queryKeys.workArea.root, 'companyUsersTree', companyId, clientId] as const,
    companyUsersSearch: (rootCompanyId: number | null, searchTerm: string) =>
      [...queryKeys.workArea.root, 'companyUsersSearch', rootCompanyId, searchTerm] as const,
  },
  client: {
    root: ['client'] as const,
    notificationUserInfo: () =>
      [...queryKeys.client.root, 'notificationUserInfo'] as const,
    notificationEmails: () =>
      [...queryKeys.client.root, 'notificationEmails'] as const,
    notificationTypes: (contactMethodId: string | null) =>
      [...queryKeys.client.root, 'notificationTypes', contactMethodId] as const,
  },
  ediAdmin: {
    root: ['ediAdmin'] as const,
    poTimingLogs: () => [...queryKeys.ediAdmin.root, 'poTimingLogs'] as const,
    outstandingInvoiceLogs: () =>
      [...queryKeys.ediAdmin.root, 'outstandingInvoiceLogs'] as const,
    userManual: () => [...queryKeys.ediAdmin.root, 'userManual'] as const,
  },
};
